import React, { useEffect, useMemo, useState } from "react";
import { getCompaniesLite } from "../services/companyService";
import {
  getSystemOverview,
  getAiUsage,
  getWeeklyAiUsage,
} from "../services/systemAuditService";
import {
  ACTION_LOGS,
  LOGIN_SUMMARY,
  DAILY_LOGINS,
  LOGIN_LOGS,
} from "../data/companyAuditLogs";
import { toast } from "../utils/toast";
import "../styles/company-audit-logs.css";

const PERIODS = ["7D", "30D", "90D", "YTD"];
const PERIOD_DAYS = { "7D": 7, "30D": 30, "90D": 90 };
const PAGE_SIZE = 5;

const fmt = (n) => Number(n || 0).toLocaleString();
const initials = (text = "") => text.slice(0, 2).toUpperCase();
const percent = (part, total) =>
  total ? `${((part / total) * 100).toFixed(1)}%` : "0%";
const shortDay = (date) =>
  date.toLocaleDateString(undefined, { month: "short", day: "2-digit" });

// Local YYYY-MM-DD (toISOString would shift the date by the UTC offset).
const isoDate = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

const periodStart = (period) => {
  const d = new Date();
  if (period === "YTD") return `${d.getFullYear()}-01-01`;
  d.setDate(d.getDate() - (PERIOD_DAYS[period] - 1));
  return isoDate(d);
};

// The weekly endpoint omits days with no usage; fill all 7 so the chart has no gaps.
const fillLastSevenDays = (rows) => {
  const byDate = Object.fromEntries(
    rows.map((r) => [String(r.date).slice(0, 10), Number(r.total_tokens) || 0]),
  );
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date();
    d.setDate(d.getDate() - (6 - i));
    return { day: shortDay(d), value: byDate[isoDate(d)] || 0 };
  });
};

// Roll AI usage records up into per-feature rows and top users.
const summarizeAiUsage = (records) => {
  const features = {};
  const users = {};
  records.forEach((r) => {
    const f = (features[r.use_type] ||= {
      feature: r.use_type,
      models: new Set(),
      requests: 0,
      input: 0,
      output: 0,
      total: 0,
    });
    if (r.model) f.models.add(r.model);
    f.requests += 1;
    f.input += Number(r.input_tokens) || 0;
    f.output += Number(r.output_tokens) || 0;
    f.total += Number(r.total_tokens) || 0;

    const u = (users[r.user_id] ||= {
      id: r.user_id,
      name: r.user?.name || `User #${r.user_id}`,
      email: r.user?.email || "",
      requests: 0,
      tokens: 0,
    });
    u.requests += 1;
    u.tokens += Number(r.total_tokens) || 0;
  });
  const featureRows = Object.values(features).sort((a, b) => b.total - a.total);
  return {
    features: featureRows,
    topUsers: Object.values(users)
      .sort((a, b) => b.tokens - a.tokens)
      .slice(0, 4),
    input: featureRows.reduce((s, f) => s + f.input, 0),
    output: featureRows.reduce((s, f) => s + f.output, 0),
  };
};

const EmptyNote = ({ children }) => <p className="cal-empty-note">{children}</p>;

// Collapsible card used for each section of the page.
const Section = ({ title, subtitle, badge, children }) => {
  const [open, setOpen] = useState(true);
  return (
    <section className="cal-section">
      <button
        type="button"
        className="cal-section-head"
        onClick={() => setOpen((v) => !v)}
      >
        <div>
          <h3 className="cal-section-title">
            {title}
            {badge && <span className="cal-badge">{badge}</span>}
          </h3>
          {subtitle && <p className="cal-section-sub">{subtitle}</p>}
        </div>
        <span className={`cal-caret ${open ? "open" : ""}`}>▾</span>
      </button>
      {open && <div className="cal-section-body">{children}</div>}
    </section>
  );
};

const Tile = ({ label, value, note, delta, tone = "blue" }) => (
  <div className={`cal-tile tone-${tone}`}>
    <div className="cal-tile-top">
      <span className="cal-tile-label">{label}</span>
      {delta && <span className="cal-tile-delta">{delta}</span>}
    </div>
    <div className="cal-tile-value">{fmt(value)}</div>
    {note && <div className="cal-tile-note">{note}</div>}
  </div>
);

// Simple CSS bar chart; each bar can be stacked with an optional red "fail" part.
const BarChart = ({ data, valueKey, failKey }) => {
  const max =
    Math.max(...data.map((d) => d[valueKey] + (d[failKey] || 0))) || 1;
  return (
    <div className="cal-bars">
      {data.map((d) => (
        <div
          key={d.day}
          className="cal-bar-col"
          title={`${d.day}: ${d[valueKey]}${failKey ? ` ok / ${d[failKey]} failed` : ""}`}
        >
          <div className="cal-bar-stack">
            {failKey && (
              <div
                className="cal-bar fail"
                style={{ height: `${(d[failKey] / max) * 100}%` }}
              />
            )}
            <div
              className="cal-bar"
              style={{ height: `${(d[valueKey] / max) * 100}%` }}
            />
          </div>
          <span className="cal-bar-label">{d.day}</span>
        </div>
      ))}
    </div>
  );
};

const exportCsv = (records) => {
  const header = ["Date", "User", "Email", "Company", "Feature", "Model", "Input", "Output", "Total"];
  const lines = records.map((r) =>
    [
      r.created_at,
      r.user?.name,
      r.user?.email,
      r.company?.name,
      r.use_type,
      r.model,
      r.input_tokens,
      r.output_tokens,
      r.total_tokens,
    ]
      .map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`)
      .join(","),
  );
  const blob = new Blob([[header.join(","), ...lines].join("\n")], {
    type: "text/csv",
  });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = `ai-usage-${isoDate(new Date())}.csv`;
  a.click();
  URL.revokeObjectURL(a.href);
};

const CompanyAuditLogsPage = () => {
  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState("");
  const [period, setPeriod] = useState("30D");
  const [actionFilter, setActionFilter] = useState("ALL");
  const [entityFilter, setEntityFilter] = useState("ALL");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [overview, setOverview] = useState(null);
  const [aiUsage, setAiUsage] = useState({ totalTokens: 0, records: [] });
  const [weekly, setWeekly] = useState([]);
  const [aiLoading, setAiLoading] = useState(true);

  useEffect(() => {
    getCompaniesLite().then(setCompanies);
    getSystemOverview()
      .then(setOverview)
      .catch(() => toast.error("Failed to load system overview"));
  }, []);

  // AI usage depends on the selected company and period; weekly only on company.
  useEffect(() => {
    let active = true;
    const companyFilter = companyId || undefined;
    setAiLoading(true);
    Promise.all([
      getAiUsage({ companyId: companyFilter, fromDate: periodStart(period) }),
      getWeeklyAiUsage({ companyId: companyFilter }),
    ])
      .then(([usage, week]) => {
        if (!active) return;
        setAiUsage(usage);
        setWeekly(fillLastSevenDays(week));
      })
      .catch(() => active && toast.error("Failed to load AI usage"))
      .finally(() => active && setAiLoading(false));
    return () => {
      active = false;
    };
  }, [companyId, period]);

  const ai = useMemo(() => summarizeAiUsage(aiUsage.records), [aiUsage]);
  const requestCount = aiUsage.records.length;

  const entityTypes = useMemo(
    () => [...new Set(ACTION_LOGS.map((l) => l.entity))],
    [],
  );

  const actionCounts = useMemo(() => {
    const counts = { CREATE: 0, UPDATE: 0, DELETE: 0 };
    ACTION_LOGS.forEach((l) => (counts[l.action] += 1));
    return counts;
  }, []);

  const filteredLogs = useMemo(() => {
    const q = search.trim().toLowerCase();
    return ACTION_LOGS.filter(
      (l) =>
        (actionFilter === "ALL" || l.action === actionFilter) &&
        (entityFilter === "ALL" || l.entity === entityFilter) &&
        (!q ||
          l.user.toLowerCase().includes(q) ||
          l.entityId.toLowerCase().includes(q)),
    );
  }, [actionFilter, entityFilter, search]);

  const totalPages = Math.max(1, Math.ceil(filteredLogs.length / PAGE_SIZE));
  const pageLogs = filteredLogs.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  // Reset to first page whenever the filters change.
  useEffect(() => setPage(1), [actionFilter, entityFilter, search]);

  return (
    <div className="cal-page">
      {/* Header */}
      <div className="cal-header">
        <div>
          <h2 className="cal-title">Company Audit Logs</h2>
          <p className="cal-subtitle">
            Activity, AI usage and security events for a company
          </p>
          <div className="cal-company-row">
            <label htmlFor="cal-company">Company</label>
            <select
              id="cal-company"
              className="cal-select"
              value={companyId}
              onChange={(e) => setCompanyId(e.target.value)}
            >
              <option value="">All companies</option>
              {companies.map((c) => (
                <option key={c.company_id} value={c.company_id}>
                  {c.name}
                  {c.companyCode ? ` (${c.companyCode})` : ""}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="cal-header-actions">
          <div className="cal-periods">
            {PERIODS.map((p) => (
              <button
                key={p}
                type="button"
                className={`cal-period ${period === p ? "active" : ""}`}
                onClick={() => setPeriod(p)}
              >
                {p}
              </button>
            ))}
          </div>
          <button
            type="button"
            className="cal-btn primary"
            disabled={!requestCount}
            onClick={() => exportCsv(aiUsage.records)}
          >
            Export AI Usage (CSV)
          </button>
        </div>
      </div>

      {/* Section 1: System overview (GET /system-audit — system-wide) */}
      <Section title="System Overview" subtitle="System-wide totals across all companies">
        {overview ?
          <div className="cal-footprint">
            {[
              ["Companies", overview.companies],
              ["Users", overview.users],
              ["Jobs", overview.jobs],
              ["Job PAEIs", overview.jobPaeis],
              ["AI Tokens", overview.aiTokens],
            ].map(([label, value]) => (
              <div key={label} className="cal-mini">
                <div className="cal-mini-value">{fmt(value)}</div>
                <div className="cal-mini-label">{label}</div>
              </div>
            ))}
          </div>
        : <EmptyNote>Loading overview…</EmptyNote>}
      </Section>

      {/* Section 2: AI usage (GET /system-audit/ai-usage + /weekly) */}
      <Section
        title="AI Usage & Token Consumption"
        subtitle={`Requests and tokens by AI feature · ${period}`}
        badge={companyId ? "Selected company" : "All companies"}
      >
        {aiLoading ?
          <EmptyNote>Loading AI usage…</EmptyNote>
        : <>
            <div className="cal-tiles">
              <Tile
                label="AI Requests"
                value={requestCount}
                note={`Across ${ai.features.length} features`}
              />
              <Tile
                label="Total Tokens"
                value={aiUsage.totalTokens}
                note={`Avg ${fmt(Math.round(aiUsage.totalTokens / (requestCount || 1)))} / request`}
              />
              <Tile
                label="Input Tokens"
                value={ai.input}
                note={`${percent(ai.input, aiUsage.totalTokens)} of total`}
              />
              <Tile
                label="Output Tokens"
                value={ai.output}
                note={`${percent(ai.output, aiUsage.totalTokens)} of total`}
              />
            </div>

            <div className="cal-split">
              <div className="cal-panel">
                <h4 className="cal-panel-title">AI Features</h4>
                {ai.features.length ?
                  <div className="cal-table-wrap">
                    <table className="cal-table">
                      <thead>
                        <tr>
                          <th>Feature</th>
                          <th>Model</th>
                          <th className="num">Requests</th>
                          <th className="num">In Tokens</th>
                          <th className="num">Out Tokens</th>
                          <th className="num">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {ai.features.map((f) => (
                          <tr key={f.feature}>
                            <td className="cal-strong">{f.feature}</td>
                            <td className="cal-muted">
                              {[...f.models].join(", ") || "-"}
                            </td>
                            <td className="num">{fmt(f.requests)}</td>
                            <td className="num">{fmt(f.input)}</td>
                            <td className="num">{fmt(f.output)}</td>
                            <td className="num cal-strong">{fmt(f.total)}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr>
                          <td colSpan={2}>Total</td>
                          <td className="num">{fmt(requestCount)}</td>
                          <td className="num">{fmt(ai.input)}</td>
                          <td className="num">{fmt(ai.output)}</td>
                          <td className="num">{fmt(aiUsage.totalTokens)}</td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                : <EmptyNote>No AI usage recorded for this period.</EmptyNote>}
              </div>

              <div className="cal-panel">
                <h4 className="cal-panel-title">Tokens · Last 7 Days</h4>
                <BarChart data={weekly} valueKey="value" />
              </div>
            </div>

            {ai.topUsers.length > 0 && (
              <>
                <h4 className="cal-panel-title">Top AI Users</h4>
                <div className="cal-users">
                  {ai.topUsers.map((u) => (
                    <div key={u.id} className="cal-user">
                      <span className="cal-avatar">{initials(u.name)}</span>
                      <div className="cal-user-info">
                        <div className="cal-strong cal-ellipsis">{u.name}</div>
                        <div className="cal-muted cal-ellipsis">
                          {u.email} · {fmt(u.requests)} req
                        </div>
                        <div className="cal-link">{fmt(u.tokens)} tokens</div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </>
        }
      </Section>

      {/* Section 3: Entity action log */}
      <Section
        title="Entity Action Log"
        subtitle="Create, update and delete events on company entities"
        badge="Sample data"
      >
        <div className="cal-tiles">
          <Tile label="Total Actions" value={ACTION_LOGS.length} />
          <Tile label="Creates" value={actionCounts.CREATE} tone="green" />
          <Tile label="Updates" value={actionCounts.UPDATE} tone="blue" />
          <Tile label="Deletes" value={actionCounts.DELETE} tone="red" />
        </div>

        <div className="cal-filters">
          <select
            className="cal-select"
            value={actionFilter}
            onChange={(e) => setActionFilter(e.target.value)}
          >
            <option value="ALL">All Actions</option>
            <option value="CREATE">Create</option>
            <option value="UPDATE">Update</option>
            <option value="DELETE">Delete</option>
          </select>
          <select
            className="cal-select"
            value={entityFilter}
            onChange={(e) => setEntityFilter(e.target.value)}
          >
            <option value="ALL">All Entity Types</option>
            {entityTypes.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
          <input
            className="cal-search"
            placeholder="Search by user or entity ID..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div className="cal-table-wrap">
          <table className="cal-table">
            <thead>
              <tr>
                <th>Timestamp</th>
                <th>User</th>
                <th>Action</th>
                <th>Entity</th>
                <th>Entity ID</th>
                <th>Details</th>
              </tr>
            </thead>
            <tbody>
              {pageLogs.map((l) => (
                <tr key={`${l.time}-${l.entityId}`}>
                  <td className="cal-mono">{l.time}</td>
                  <td>{l.user}</td>
                  <td>
                    <span className={`cal-pill ${l.action.toLowerCase()}`}>
                      {l.action}
                    </span>
                  </td>
                  <td className="cal-strong">{l.entity}</td>
                  <td className="cal-mono cal-link">#{l.entityId}</td>
                  <td className="cal-details">{l.details}</td>
                </tr>
              ))}
              {pageLogs.length === 0 && (
                <tr>
                  <td colSpan={6} className="cal-empty">
                    No actions match your filters
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="cal-pager">
          <span className="cal-muted">
            Showing {pageLogs.length} of {filteredLogs.length} entries
          </span>
          <div className="cal-pager-btns">
            <button
              type="button"
              disabled={page === 1}
              onClick={() => setPage((p) => p - 1)}
            >
              ‹
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((n) => (
              <button
                key={n}
                type="button"
                className={n === page ? "active" : ""}
                onClick={() => setPage(n)}
              >
                {n}
              </button>
            ))}
            <button
              type="button"
              disabled={page === totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              ›
            </button>
          </div>
        </div>
      </Section>

      {/* Section 4: Security & login */}
      <Section
        title="Security & Login Audit"
        subtitle="Sign-in activity and failed access attempts"
        badge="Sample data"
      >
        <div className="cal-tiles">
          {LOGIN_SUMMARY.map((t) => (
            <Tile key={t.label} {...t} />
          ))}
        </div>

        <div className="cal-split reverse">
          <div className="cal-panel">
            <h4 className="cal-panel-title">Login Attempts</h4>
            <p className="cal-muted">Success (blue) vs failed (red)</p>
            <BarChart data={DAILY_LOGINS} valueKey="success" failKey="failed" />
          </div>

          <div className="cal-panel">
            <h4 className="cal-panel-title">Recent Logins</h4>
            <div className="cal-table-wrap">
              <table className="cal-table">
                <thead>
                  <tr>
                    <th>Timestamp</th>
                    <th>User</th>
                    <th>Status</th>
                    <th>Role</th>
                    <th>IP / Location</th>
                    <th>Reason</th>
                  </tr>
                </thead>
                <tbody>
                  {LOGIN_LOGS.map((l) => (
                    <tr key={`${l.time}-${l.email}`}>
                      <td className="cal-mono">{l.time}</td>
                      <td>{l.email}</td>
                      <td>
                        <span className={`cal-pill ${l.status.toLowerCase()}`}>
                          {l.status}
                        </span>
                      </td>
                      <td>{l.role}</td>
                      <td>
                        <div className="cal-mono">{l.ip}</div>
                        <div className="cal-muted">{l.location}</div>
                      </td>
                      <td className={l.reason ? "cal-danger" : "cal-muted"}>
                        {l.reason || "-"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </Section>
    </div>
  );
};

export default CompanyAuditLogsPage;
